import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Pagination,
  Stack,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import Header from '@/component/Header';
import { Tables } from '@/component/pools/table/Tables';
import { StakeDbPoolListTableColumns } from '@/component/stake-db/table/StakeDbColumnsData';
import { readStakeDbPools } from '@/api/stake-db';
import { StakeDbPool, ReadStakeDbPoolsResponse } from '@/types/stake-db';
import { stakeDbStateFilter, stakeDbIsHiddenFilter, stakeDbStatusSelect } from '@/lib/filter/stakeDbPoolState';

export default function StakeDbPoolListCmp() {
  const navigate = useNavigate();
  const [list, setList] = useState<(StakeDbPool & { stateFilter: string; isHiddenFilter: string })[]>([]);
  const [stateFilter, setStateFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const limit = 10;

  const handleRewardClick = (poolId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate('/stake-db-reward-period', { state: { poolId } });
  };

  const columnsWithReward: GridColDef[] = [
    ...StakeDbPoolListTableColumns,
    {
      minWidth: 100,
      field: 'reward',
      headerName: 'Reward',
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Button
          variant="contained"
          size="small"
          onClick={(e) => handleRewardClick(params.row.poolId, e)}
        >
          리워드
        </Button>
      ),
    },
  ];

  const fetchPools = async () => {
    setLoading(true);
    try {
      const params: any = {
        page,
        limit,
      };
      if (stateFilter !== '') {
        params.state = Number(stateFilter);
      }

      const re = await readStakeDbPools(params);
      if (re.status !== 200) {
        return;
      }

      const response: ReadStakeDbPoolsResponse = re.data;
      const pools = response.pools.map((item: any) => ({
        ...item,
        stateFilter: stakeDbStateFilter(item.state),
        isHiddenFilter: stakeDbIsHiddenFilter(item.isHidden),
      }));
      setList(pools);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error('Failed to fetch pools:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPools();
  }, [page, stateFilter]);

  const handleStateFilterChange = (event: SelectChangeEvent) => {
    setStateFilter(event.target.value);
    setPage(1);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        overflow: 'auto',
        flex: 1,
        flexDirection: 'column',
        gap: { xs: '16px', sm: '0px' },
      }}
    >
      <Card>
        <Header title="Stake-db Pool List" />
        <Divider />
        <CardContent>
          <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>State Filter</InputLabel>
              <Select
                value={stateFilter}
                label="State Filter"
                onChange={handleStateFilterChange}
              >
                <MenuItem value="">All</MenuItem>
                {stakeDbStatusSelect.map((option) => (
                  <MenuItem key={option.value} value={String(option.value)}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              maxHeight: 600,
              overflow: 'auto',
            }}
          >
            {!loading && (
              <Tables
                list={list}
                columns={columnsWithReward}
                navigateUrl="/stake-db-pool-detail"
                getRowId={(row) => row.poolId}
              />
            )}
          </Box>

          <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
            />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
